import React, { useEffect, useRef, useState } from 'react';
import { useNotificationStore } from '@/store/notificationStore';
import { notificationApi } from '@/services/api';
import { useNavigate } from 'react-router-dom';
import { useThemeStore } from '@/store/themeStore';
import { X, CheckCircle, XCircle, Clock, Zap, AlertTriangle, Users, Bell, CheckCircle2, Trash2, ChevronRight } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { Button } from '@/components/ui/Button';

export default function NotificationPanel() {
  const { notifications, unreadCount, isOpen, closePanel, markRead, markAllRead, clearAll, setNotifications } = useNotificationStore();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const { theme } = useThemeStore();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  const [activeTab, setActiveTab] = useState<'UNREAD' | 'READ'>('UNREAD');
  const [selectedNotification, setSelectedNotification] = useState<any | null>(null);

  useEffect(() => {
    if (isOpen) {
      notificationApi.getAll({ limit: 50 }).then((res) => {
        setNotifications(res.data.data, res.data.unreadCount);
      }).catch(console.error);
    }
  }, [isOpen, setNotifications]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        // don't close if they clicked inside the modal
        if (selectedNotification) return;
        closePanel();
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closePanel, selectedNotification]);

  if (!isOpen && !selectedNotification) return null;

  const handleMarkAllRead = async () => {
    try { await notificationApi.markAllRead(); markAllRead(); } catch (err) { console.error(err); }
  };

  const handleClearAll = async () => {
    try { await notificationApi.clearAll(); clearAll(); } catch (err) { console.error(err); }
  };

  const handleNotificationClick = async (n: any) => {
    if (!n.isRead) {
      try { await notificationApi.markRead(n.id); markRead(n.id); } catch (err) { console.error(err); }
    }
    setSelectedNotification(n);
    closePanel();
  };

  const handleActionClick = () => {
    if (selectedNotification?.actionUrl) {
      navigate(selectedNotification.actionUrl);
      setSelectedNotification(null);
      closePanel();
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'LEAVE_APPROVED':    return <CheckCircle  className="w-5 h-5 text-green-500" />;
      case 'LEAVE_REJECTED':    return <XCircle      className="w-5 h-5 text-red-500" />;
      case 'APPROVAL_PENDING':  return <Clock        className="w-5 h-5 text-amber-500" />;
      case 'LEAVE_ESCALATED':   return <Zap          className="w-5 h-5 text-orange-500" />;
      case 'BALANCE_EXPIRY':    return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'BENCH_RISK_ALERT':  return <Users        className="w-5 h-5 text-red-500" />;
      default:                  return <Bell         className="w-5 h-5 text-blue-500" />;
    }
  };

  // Panel surface colours
  const panelBg     = isDark ? '#151f2e' : '#ffffff';
  const borderCol   = isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9';
  const headerBg    = isDark ? '#0f172a' : '#f8fafc';
  const unreadItemBg     = isDark ? 'rgba(59,130,246,0.1)'  : 'rgba(239,246,255,0.8)';
  const unreadItemBorder = isDark ? 'rgba(59,130,246,0.25)' : 'rgba(147,197,253,0.6)';
  const hoverBg     = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc';
  const unreadHover = isDark ? 'rgba(59,130,246,0.15)' : '#eff6ff';

  const displayNotifications = notifications.filter(n => activeTab === 'UNREAD' ? !n.isRead : n.isRead);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-40" onClick={closePanel} />
      )}

      {/* Notification Modal */}
      {selectedNotification && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setSelectedNotification(null)} />
          <div className={`relative w-full max-w-lg rounded-2xl shadow-2xl p-6 ${isDark ? 'bg-slate-800 text-white' : 'bg-white text-slate-800'} animate-fade-in`}>
            <button 
              onClick={() => setSelectedNotification(null)}
              className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-6 pr-8">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-50/10">
                {getIcon(selectedNotification.type)}
              </div>
              <div>
                <h3 className="text-lg font-bold leading-tight">{selectedNotification.title}</h3>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {format(new Date(selectedNotification.createdAt), 'PPpp')}
                </p>
              </div>
            </div>
            
            <div className={`p-4 rounded-xl text-sm leading-relaxed mb-6 whitespace-pre-wrap ${isDark ? 'bg-slate-900/50 text-slate-300' : 'bg-slate-50 text-slate-700'}`}>
              <div dangerouslySetInnerHTML={{ __html: selectedNotification.body }} />
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setSelectedNotification(null)}>Close</Button>
              {selectedNotification.actionUrl && (
                <Button variant="primary" onClick={handleActionClick}>
                  Take Action <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Panel */}
      {isOpen && (
        <div
          ref={panelRef}
        className="fixed inset-y-0 right-0 w-full max-w-sm z-50 flex flex-col animate-slide-in-right shadow-2xl"
        style={{ backgroundColor: panelBg, borderLeft: `1px solid ${borderCol}` }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ backgroundColor: headerBg, borderColor: borderCol }}
        >
          <div className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: isDark ? 'rgba(59,130,246,0.15)' : '#dbeafe' }}
            >
              <Bell className="w-4 h-4" style={{ color: isDark ? '#60a5fa' : '#2563eb' }} />
            </div>
            <h2 className="text-base font-bold" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
              Notifications
            </h2>
            {unreadCount > 0 && activeTab === 'UNREAD' && (
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: isDark ? 'rgba(59,130,246,0.2)' : '#dbeafe',
                  color: isDark ? '#93c5fd' : '#1d4ed8',
                }}
              >
                {unreadCount}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {activeTab === 'UNREAD' && unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors"
                style={{ color: isDark ? '#64748b' : '#64748b' }}
                onMouseEnter={e => (e.currentTarget.style.color = isDark ? '#60a5fa' : '#2563eb')}
                onMouseLeave={e => (e.currentTarget.style.color = isDark ? '#64748b' : '#64748b')}
                title="Mark all as read"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Mark all read</span>
              </button>
            )}
            {activeTab === 'READ' && displayNotifications.length > 0 && (
              <button
                onClick={handleClearAll}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors"
                style={{ color: isDark ? '#64748b' : '#64748b' }}
                onMouseEnter={e => (e.currentTarget.style.color = isDark ? '#f87171' : '#ef4444')}
                onMouseLeave={e => (e.currentTarget.style.color = isDark ? '#64748b' : '#64748b')}
                title="Clear all"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Clear all</span>
              </button>
            )}
            <button
              onClick={closePanel}
              className="p-1.5 rounded-lg transition-colors ml-1"
              style={{ color: isDark ? '#64748b' : '#94a3b8' }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9';
                (e.currentTarget as HTMLElement).style.color = isDark ? '#e2e8f0' : '#1e293b';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                (e.currentTarget as HTMLElement).style.color = isDark ? '#64748b' : '#94a3b8';
              }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex p-2 gap-2 border-b" style={{ borderColor: borderCol, backgroundColor: headerBg }}>
          <button 
            onClick={() => setActiveTab('UNREAD')}
            className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${activeTab === 'UNREAD' ? 'bg-blue-500 text-white shadow-sm' : ''}`}
            style={activeTab !== 'UNREAD' ? { color: isDark ? '#94a3b8' : '#64748b' } : {}}
          >
            Unread
          </button>
          <button 
            onClick={() => setActiveTab('READ')}
            className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${activeTab === 'READ' ? (isDark ? 'bg-slate-700 text-white shadow-sm' : 'bg-slate-200 text-slate-800 shadow-sm') : ''}`}
            style={activeTab !== 'READ' ? { color: isDark ? '#94a3b8' : '#64748b' } : {}}
          >
            Read
          </button>
        </div>

        {/* Notification list */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {displayNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-4">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9' }}
              >
                <Bell className="w-8 h-8" style={{ color: isDark ? '#334155' : '#cbd5e1' }} />
              </div>
              <h3 className="font-semibold mb-1" style={{ color: isDark ? '#94a3b8' : '#475569' }}>
                No {activeTab.toLowerCase()} notifications
              </h3>
              <p className="text-sm" style={{ color: isDark ? '#475569' : '#94a3b8' }}>
                {activeTab === 'UNREAD' ? "We'll let you know when there's an update." : "You haven't read any notifications yet."}
              </p>
            </div>
          ) : (
            displayNotifications.map((n) => (
              <div
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                className="p-3 rounded-xl cursor-pointer flex gap-3 relative transition-all duration-150"
                style={{
                  backgroundColor: n.isRead ? 'transparent' : unreadItemBg,
                  border: n.isRead ? '1px solid transparent' : `1px solid ${unreadItemBorder}`,
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = n.isRead ? hoverBg : unreadHover;
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = n.isRead ? 'transparent' : unreadItemBg;
                }}
              >
                {/* Unread dot */}
                {!n.isRead && (
                  <span className="absolute top-4 left-2 w-1.5 h-1.5 bg-blue-500 rounded-full" />
                )}

                {/* Icon */}
                <div className={`mt-0.5 shrink-0 ${!n.isRead ? 'ml-2' : ''}`}>
                  {getIcon(n.type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-0.5">
                    <p
                      className="text-sm leading-snug"
                      style={{
                        color: n.isRead ? (isDark ? '#94a3b8' : '#475569') : (isDark ? '#e2e8f0' : '#0f172a'),
                        fontWeight: n.isRead ? 400 : 600,
                      }}
                    >
                      {n.title}
                    </p>
                    <span
                      className="text-[10px] whitespace-nowrap shrink-0 mt-0.5"
                      style={{ color: isDark ? '#475569' : '#94a3b8' }}
                    >
                      {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                  <p
                    className="text-xs line-clamp-2 leading-snug"
                    style={{ color: isDark ? '#64748b' : '#64748b' }}
                  >
                    {n.body.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {displayNotifications.length > 0 && (
          <div
            className="px-5 py-3 text-center border-t"
            style={{ borderColor: borderCol, backgroundColor: headerBg }}
          >
            <span className="text-xs" style={{ color: isDark ? '#475569' : '#94a3b8' }}>
              {displayNotifications.length} notification{displayNotifications.length !== 1 ? 's' : ''} shown
            </span>
          </div>
        )}
      </div>
      )}
    </>
  );
}
